import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "map_panel_collapsed:";

// Storage may be unavailable (privacy mode, quota) — fall back to "expanded"
// and keep the state in memory only, same defensive read as the company slice.
function readPersisted(storageKey: string): boolean {
  try {
    return localStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

/**
 * Collapsed/expanded state of a page's map panel, remembered per browser under
 * `panelKey`. The pain point is a per-device one (the table does not fit on a
 * laptop screen), so it is a device preference — not URL state, not an org setting.
 */
export function useMapPanelCollapsed(panelKey: string) {
  const storageKey = `${STORAGE_PREFIX}${panelKey}`;
  const [collapsed, setCollapsed] = useState(() => readPersisted(storageKey));

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, collapsed ? "1" : "0");
    } catch {
      // Storage unavailable — the in-memory state still drives the UI.
    }
  }, [collapsed, storageKey]);

  const toggle = useCallback(() => setCollapsed((prev) => !prev), []);

  return { collapsed, toggle };
}
